import React from 'react';
import {
	components,
} from 'react-select';

/**
 * Converts a value to a URL-friendly slug for test IDs
 * e.g., "Local Weather" -> "local-weather", "Pearl Media" -> "pearl-media"
 * @param {*} value - The value to convert
 * @returns {string} - URL-friendly slug
 */
export const toSlug = (value) => {
	if (value === null || value === undefined) return '0';
	const slug = String(value)
		.toLowerCase()
		.trim()
		.replace(/\s+/g, '-')       // spaces to dashes
		.replace(/_/g, '-')         // underscores to dashes for snake_case labels
		.replace(/[^a-z0-9-]/g, '') // remove non-alphanumeric (except dashes)
		.replace(/-+/g, '-')        // collapse multiple dashes
		.replace(/^-+|-+$/g, '');   // trim all leading/trailing dashes
	return slug || '0';             // ensure non-empty, stable fallback
};

/**
 * Stable DropdownIndicator component for react-select that reads test ID from selectProps.
 * Usage: <Select components={{ DropdownIndicator }} dropdownIndicatorTestId="my-test-id" />
 */
export const DropdownIndicator = (props) => {
	const testId = props.selectProps?.dropdownIndicatorTestId;
	return (
		<components.DropdownIndicator
			{...props}
			innerProps={{
				...props.innerProps,
				...(testId && {
					'data-test-id': testId,
				}),
			}}
		/>
	);
};
DropdownIndicator.displayName = 'DropdownIndicator';

/**
 * Stable Option component for react-select that reads test ID prefix from selectProps.
 * Usage: <Select components={{ Option }} optionTestIdPrefix="option" />
 */
export const Option = (props) => {
	const testIdPrefix = props.selectProps?.optionTestIdPrefix;
	if (!testIdPrefix) {
		return <components.Option {...props} />;
	}
	const label = props.data?.label;
	const identifier = label ? toSlug(label) : '0';
	return (
		<components.Option
			{...props}
			innerProps={{
				...props.innerProps,
				'data-test-id': `${testIdPrefix}-${identifier}`,
			}}
		/>
	);
};
Option.displayName = 'Option';

/**
 * Renders a multi-select's whole selection as one line of comma-separated labels
 * instead of a stack of removable chips, so the closed control reads as a text field
 * rather than an already-open dropdown. Selections are changed in the menu, which
 * lists every option with the chosen ones marked (hideSelectedOptions must stay false).
 *
 * react-select calls MultiValue once per selected value; only index 0 renders.
 */
export const MultiValueSummary = (props) => {
	if (props.index > 0) {
		return null;
	}
	if (props.selectProps && props.selectProps.inputValue) {
		return null;
	}
	const labels = props.getValue().map((option) => option.label).filter(Boolean);
	const text = labels.join(', ');
	return (
		<span
			data-test-id="multi-value-summary"
			title={text}
			style={{
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				minWidth: 0,
			}}
		>
			{text}
		</span>
	);
};
MultiValueSummary.displayName = 'MultiValueSummary';

/**
 * Pre-configured components object for convenience.
 * Usage: <Select components={selectComponents} dropdownIndicatorTestId="x" optionTestIdPrefix="option" />
 */
export const selectComponents = {
	DropdownIndicator,
	Option,
};

/**
 * selectComponents for a multi-select, with the chip stack collapsed to a summary line.
 */
export const multiSelectComponents = {
	DropdownIndicator,
	Option,
	MultiValue: MultiValueSummary,
};

/**
 * Spread this onto a multi-select rather than passing `components` alone.
 *
 * `hideSelectedOptions` is **mandatory** with MultiValueSummary, not a preference:
 * react-select defaults it to `true` for isMulti, and with the removable chips gone the
 * menu is the only place a selection can be undone. Omit it and the menu renders "No
 * options" once everything is picked — the selection becomes individually unremovable
 * and only clear-all works. Keeping the two together in one object is what stops a
 * future multi-select from copying the components and dropping the prop.
 *
 * `closeMenuOnSelect` is `false` so several categories can be ticked in one pass — with the
 * chips gone the menu is the only editing surface, and closing after each pick made dropping
 * three of eight categories six clicks instead of three. DEV-11748 QA round 2 reported the
 * open menu covering the fields below it; that is held off by `maxMenuHeight` and
 * `menuPlacement="auto"` at the call site, which cap and flip the list rather than dismissing
 * it. Keep those two whenever this object is used.
 */
export const multiSelectSummaryProps = {
	components:          multiSelectComponents,
	hideSelectedOptions: false,
	closeMenuOnSelect:   false,
};
